import React, { useState, useMemo, useCallback } from 'react';
import { FileEarmarkPdf, Save } from 'react-bootstrap-icons';
import { FaScaleBalanced, FaDoorOpen, FaTrophy } from 'react-icons/fa6';
import { SelectableShift, UserStats, IndividualDistributionMode, IndividualSavePayload } from '../../types';
import './IndividualTipView.scss';

// ────────────────────────────────────────────
// Mode configuration
// ────────────────────────────────────────────
const MODE_CONFIG: Record<IndividualDistributionMode, {
  label: string;
  shortLabel: string;
  description: string;
  icon: React.ComponentType<{ size?: number }>;
}> = {
  equal_split: {
    label: 'Distribución Equitativa',
    shortLabel: 'Equitativa',
    description: 'Las propinas se dividen equitativamente entre todos los meseros que atendieron cada mesa.',
    icon: FaScaleBalanced,
  },
  table_opener: {
    label: 'Por Apertura de Mesa',
    shortLabel: 'Apertura',
    description: 'El 100% de la propina se asigna al mesero que abrió la mesa.',
    icon: FaDoorOpen,
  },
  top_contributor: {
    label: 'Por Mayor Contribución',
    shortLabel: 'Contribución',
    description: 'Las propinas se distribuyen proporcionalmente al volumen de ventas de cada mesero.',
    icon: FaTrophy,
  },
};

const MODES: IndividualDistributionMode[] = ['equal_split', 'table_opener', 'top_contributor'];

// ────────────────────────────────────────────
// Props & internal types
// ────────────────────────────────────────────
interface IndividualTipViewProps {
  selectedShifts: SelectableShift[];
  onBack: () => void;
  onSave?: (payload: IndividualSavePayload) => void;
  isSaving?: boolean;
  isViewMode?: boolean;
  initialMode?: IndividualDistributionMode;
  /** When viewing a saved record, the full saved payload is passed here */
  savedData?: IndividualSavePayload;
}

interface ShiftTipDetail {
  shiftName: string;
  shiftDate: string;
  tip: number;
}

interface UserRow {
  userId: number;
  userName: string;
  totalTip: number;
  shiftCount: number;
  avgTip: number;
  shifts: ShiftTipDetail[];
}

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────
const IndividualTipView: React.FC<IndividualTipViewProps> = ({
  selectedShifts,
  onBack,
  onSave,
  isSaving = false,
  isViewMode = false,
  initialMode = 'equal_split',
  savedData,
}) => {
  const [mode, setMode] = useState<IndividualDistributionMode>(initialMode);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());

  // When viewing a saved record, lock the mode to what was saved
  const isModeLocked = !!savedData;

  // Date range
  const dateRange = useMemo(() => {
    if (savedData) {
      return { start: savedData.start_date, end: savedData.end_date };
    }
    if (selectedShifts.length === 0) return { start: '', end: '' };
    const sorted = [...selectedShifts].sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
    return {
      start: sorted[0].start_time.split(' ')[0],
      end: sorted[sorted.length - 1].end_time.split(' ')[0],
    };
  }, [selectedShifts, savedData]);

  // ─── Compute user rows ───
  // When savedData is present, render directly from the saved users array.
  // Otherwise, compute from shift data + selected mode.
  const { userRows, totalTip, totalShiftsWithTip } = useMemo(() => {
    // ── Saved record path ──
    if (savedData) {
      const rows: UserRow[] = (savedData.users || []).map(u => ({
        userId: u.user_id,
        userName: u.user_name,
        totalTip: u.total_tip,
        shiftCount: u.shift_count,
        avgTip: u.avg_tip,
        shifts: [], // No per-shift breakdown available from saved data
      }));
      return {
        userRows: rows,
        totalTip: savedData.total_tips,
        totalShiftsWithTip: savedData.shift_ids?.length || 0,
      };
    }

    // ── Live computation path ──
    // Step 1: Aggregate base data per user across all shifts
    const userMap = new Map<number, {
      userName: string;
      tipByField: number;        // sum of tip or tip_as_opener
      totalSales: number;         // sum of total (for proportional mode)
      shifts: ShiftTipDetail[];
    }>();

    let grandTotalByField = 0;
    let grandTotalSales = 0;
    let shiftsWithTip = 0;

    // Determine which field to read for direct modes
    // tip_as_opener may be absent in older backend data — fallback to 0
    const getTipValue = (stats: UserStats): number => {
      if (mode === 'table_opener') {
        const value = stats.tip_as_opener ?? 0;
        return value;
      }
      return stats.tip || 0;
    };

    for (const shift of selectedShifts) {
      let shiftHasTip = false;
      const shiftDate = new Date(shift.start_time).toLocaleDateString('es-CL', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });

      for (const [userIdStr, stats] of Object.entries(shift.users_stats)) {
        const userId = parseInt(userIdStr, 10);
        const userName = stats.name || stats.user_name || 'Desconocido';
        const fieldValue = getTipValue(stats);
        const salesValue = stats.total || 0;

        if (fieldValue > 0 || (mode === 'top_contributor' && salesValue > 0)) {
          shiftHasTip = true;
        }

        const existing = userMap.get(userId);
        if (existing) {
          existing.tipByField += fieldValue;
          existing.totalSales += salesValue;
          if (fieldValue > 0) {
            existing.shifts.push({ shiftName: shift.name, shiftDate, tip: fieldValue });
          }
        } else {
          userMap.set(userId, {
            userName,
            tipByField: fieldValue,
            totalSales: salesValue,
            shifts: fieldValue > 0 ? [{ shiftName: shift.name, shiftDate, tip: fieldValue }] : [],
          });
        }

        grandTotalByField += fieldValue;
        grandTotalSales += salesValue;
      }

      if (shiftHasTip) shiftsWithTip++;
    }

    // Step 2: For top_contributor, use the equal-split pool and redistribute by sales
    if (mode === 'top_contributor') {
      // The pool is the total of `tip` (equal-split) across all shifts
      let pool = 0;
      for (const shift of selectedShifts) {
        for (const stats of Object.values(shift.users_stats)) {
          pool += stats.tip || 0;
        }
      }

      const rows: UserRow[] = Array.from(userMap.entries())
        .filter(([_, data]) => data.totalSales > 0)
        .map(([userId, data]) => {
          const proportion = grandTotalSales > 0 ? data.totalSales / grandTotalSales : 0;
          const computedTip = Math.round(pool * proportion);
          return {
            userId,
            userName: data.userName,
            totalTip: computedTip,
            shiftCount: data.shifts.length || 1,
            avgTip: data.shifts.length > 0 ? computedTip / data.shifts.length : computedTip,
            shifts: data.shifts.map(s => ({ ...s, tip: Math.round(s.tip * proportion) })),
          };
        })
        .sort((a, b) => b.totalTip - a.totalTip);

      return { userRows: rows, totalTip: pool, totalShiftsWithTip: shiftsWithTip };
    }

    // Step 3: For equal_split and table_opener, use data directly
    const rows: UserRow[] = Array.from(userMap.entries())
      .filter(([_, data]) => data.tipByField > 0)
      .map(([userId, data]) => ({
        userId,
        userName: data.userName,
        totalTip: data.tipByField,
        shiftCount: data.shifts.length,
        avgTip: data.shifts.length > 0 ? data.tipByField / data.shifts.length : 0,
        shifts: data.shifts,
      }))
      .sort((a, b) => b.totalTip - a.totalTip);

    return { userRows: rows, totalTip: grandTotalByField, totalShiftsWithTip: shiftsWithTip };
  }, [selectedShifts, mode, savedData]);

  const toggleUserExpand = useCallback((userId: number) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // ─── Save handler ───
  const handleSave = useCallback(() => {
    if (!onSave || userRows.length === 0 || isSaving) return;

    const payload: IndividualSavePayload = {
      type: 'individual',
      mode,
      start_date: dateRange.start,
      end_date: dateRange.end,
      total_tips: totalTip,
      total_distributed: totalTip,
      total_employees: userRows.length,
      shift_ids: selectedShifts.map(s => s.shift_id),
      users: userRows.map(u => ({
        user_id: u.userId,
        user_name: u.userName,
        total_tip: u.totalTip,
        shift_count: u.shiftCount,
        avg_tip: u.avgTip,
        share_percent: totalTip > 0 ? parseFloat(((u.totalTip / totalTip) * 100).toFixed(1)) : 0,
      })),
    };

    onSave(payload);
  }, [onSave, userRows, isSaving, mode, dateRange, totalTip, selectedShifts]);

  // ─── PDF Export ───
  const handlePrintPDF = useCallback(() => {
    if (userRows.length === 0) return;

    let localName = 'MedioPunto';
    try {
      const ctx = localStorage.getItem('UserContext');
      if (ctx) {
        const parsed = JSON.parse(ctx);
        localName = parsed.local_name || 'MedioPunto';
      }
    } catch (e) { /* ignore */ }

    const modeInfo = MODE_CONFIG[mode];

    const usersHTML = userRows.map(u => {
      const sharePercent = totalTip > 0 ? ((u.totalTip / totalTip) * 100).toFixed(1) : '0';
      return `
        <div class="pdf-user-card">
          <div class="pdf-user-header">
            <span class="pdf-user-name">${u.userName}</span>
            <span class="pdf-user-rate">${sharePercent}% · ${u.shiftCount} turno${u.shiftCount !== 1 ? 's' : ''}</span>
          </div>
          <div class="pdf-user-total">${formatCurrency(u.totalTip)}</div>
        </div>
      `;
    }).join('');

    const html = `<!DOCTYPE html>
    <html><head><meta charset="utf-8"><title>Distribución Individual de Propinas</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { padding: 20px; background: white; font-family: 'Courier New', monospace; }
      .container { max-width: 600px; margin: 0 auto; padding: 2rem; }
      .header { text-align: center; margin-bottom: 1.5rem; }
      .local-name { font-size: 1.4rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
      .title { font-size: 1.1rem; font-weight: 600; color: #FC7556; margin: 0.5rem 0; }
      .mode-badge { display: inline-block; font-size: 0.8rem; font-weight: 600; padding: 0.2rem 0.8rem; border-radius: 12px; background: rgba(252,117,86,0.12); color: #FC7556; margin-top: 0.3rem; }
      .period { font-size: 0.85rem; color: #718096; margin-top: 0.4rem; }
      .divider { border-top: 2px dashed #e2e8f0; margin: 1.5rem 0; }
      .section-title { font-size: 0.95rem; font-weight: 700; text-align: center; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem; }
      .pdf-user-card { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 0.5rem; }
      .pdf-user-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem; }
      .pdf-user-name { font-weight: 700; font-size: 0.95rem; }
      .pdf-user-rate { font-size: 0.75rem; padding: 0.2rem 0.5rem; border-radius: 10px; font-weight: 600; background: rgba(252,117,86,0.15); color: #FC7556; }
      .pdf-user-total { font-size: 1rem; font-weight: 700; color: #FC7556; text-align: right; }
      .grand-total { font-weight: 700; font-size: 1.1rem; padding: 0.75rem; background: #FC7556; color: white; border-radius: 4px; display: flex; justify-content: space-between; margin-top: 0.5rem; }
      .footer { margin-top: 2rem; padding-top: 1rem; border-top: 1px solid #e2e8f0; font-size: 0.8rem; color: #718096; text-align: center; }
      .sig-line { border-bottom: 1px solid #2d3748; width: 200px; margin: 2rem auto 0.5rem; }
      .sig-label { text-align: center; font-size: 0.8rem; color: #718096; }
      @media print { body { margin: 0; padding: 1rem; } }
      @page { margin: 1cm; }
    </style></head><body>
    <div class="container">
      <div class="header">
        <h1 class="local-name">${localName}</h1>
        <h2 class="title">Distribución Individual de Propinas</h2>
        <span class="mode-badge">${modeInfo.label}</span>
        <p class="period">${dateRange.start} — ${dateRange.end}</p>
      </div>
      <div class="divider"></div>
      <div>
        <h3 class="section-title">${modeInfo.label}</h3>
        ${usersHTML}
        <div class="divider" style="border-top: 1px dashed #e2e8f0; margin: 1rem 0;"></div>
        <div class="grand-total">
          <span>TOTAL</span>
          <span>${formatCurrency(totalTip)}</span>
        </div>
      </div>
      <div class="sig-line"></div>
      <p class="sig-label">Firma de Conformidad</p>
      <div class="footer">
        <p>Generado el ${new Date().toLocaleDateString('es-CL')} a las ${new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}</p>
        <p>MedioPunto — Sistema de Gestión</p>
      </div>
    </div>
    </body></html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor permite ventanas emergentes para exportar PDF');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }, [userRows, dateRange, totalTip, mode]);

  const currentModeConfig = MODE_CONFIG[mode];

  return (
    <div className="individual-tip-view">
      {/* Header */}
      <div className="view-header">
        <button className="btn btn-back" onClick={onBack}>
          ← Volver
        </button>
        <div className="header-info">
          <h2>{isViewMode ? 'Detalle de Distribución Individual' : 'Distribución Individual de Propinas'}</h2>
          <p>
            {dateRange.start} — {dateRange.end}
            {selectedShifts.length > 0 && (
              <> • {selectedShifts.length} turno{selectedShifts.length !== 1 ? 's' : ''}</>
            )}
          </p>
        </div>
        <div className="header-actions">
          {onSave && !isViewMode && (
            <button
              className="btn btn-save"
              onClick={handleSave}
              disabled={userRows.length === 0 || isSaving}
            >
              {isSaving ? (
                <>
                  <span className="spinner-sm"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Crear Registro
                </>
              )}
            </button>
          )}
          <button className="btn btn-export-pdf" onClick={handlePrintPDF} disabled={userRows.length === 0}>
            <FileEarmarkPdf size={18} />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Mode selector */}
      <div className="mode-selector">
        <div className="mode-tabs">
          {MODES.map((m) => {
            const config = MODE_CONFIG[m];
            const Icon = config.icon;
            return (
              <button
                key={m}
                className={`mode-tab ${mode === m ? 'active' : ''} ${isModeLocked && mode !== m ? 'disabled' : ''}`}
                onClick={() => !isModeLocked && setMode(m)}
                disabled={isModeLocked && mode !== m}
              >
                <Icon size={18} />
                <span className="mode-tab-label">{config.shortLabel}</span>
              </button>
            );
          })}
        </div>
        <p className="mode-description">
          {isModeLocked
            ? `Registro guardado con modo: ${currentModeConfig.label}`
            : currentModeConfig.description
          }
        </p>
      </div>

      {/* Summary cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <span className="summary-label">Total Propinas</span>
          <span className="summary-value primary">{formatCurrency(totalTip)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Turnos con Propina</span>
          <span className="summary-value">{totalShiftsWithTip} / {selectedShifts.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Empleados con Propina</span>
          <span className="summary-value">{userRows.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Promedio por Empleado</span>
          <span className="summary-value">{userRows.length > 0 ? formatCurrency(totalTip / userRows.length) : '$0'}</span>
        </div>
      </div>

      {/* User distribution table */}
      <div className="distribution-table-wrapper">
        <table className="distribution-table">
          <thead>
            <tr>
              <th className="col-expand"></th>
              <th className="col-user">Empleado</th>
              <th className="col-receipts">Turnos</th>
              <th className="col-avg">Promedio</th>
              <th className="col-total">Total Propinas</th>
              <th className="col-share">% del Total</th>
            </tr>
          </thead>
          <tbody>
            {userRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-row">
                  {mode === 'table_opener'
                    ? 'No hay datos de apertura de mesa en los turnos seleccionados. Revisa la consola de desarrollador (F12) para ver los datos exactos que llegaron de la API.'
                    : 'No hay datos de propinas individuales en los turnos seleccionados.'
                  }
                </td>
              </tr>
            ) : (
              userRows.map((user) => {
                const isExpanded = expandedUsers.has(user.userId);
                const sharePercent = totalTip > 0
                  ? ((user.totalTip / totalTip) * 100).toFixed(1)
                  : '0';

                return (
                  <React.Fragment key={user.userId}>
                    <tr
                      className={`user-row ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => user.shifts.length > 0 && toggleUserExpand(user.userId)}
                    >
                      <td className="col-expand">
                        {user.shifts.length > 1 && (
                          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                        )}
                      </td>
                      <td className="col-user">
                        <span className="user-name">{user.userName}</span>
                      </td>
                      <td className="col-receipts">{user.shiftCount}</td>
                      <td className="col-avg">{formatCurrency(user.avgTip)}</td>
                      <td className="col-total">
                        <span className="tip-amount">{formatCurrency(user.totalTip)}</span>
                      </td>
                      <td className="col-share">
                        <div className="share-bar-container">
                          <div className="share-bar" style={{ width: `${sharePercent}%` }} />
                          <span className="share-label">{sharePercent}%</span>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && user.shifts.map((shift, idx) => (
                      <tr key={`${user.userId}-shift-${idx}`} className="receipt-row">
                        <td className="col-expand"></td>
                        <td className="col-user receipt-detail">
                          <span className="receipt-id">{shift.shiftName}</span>
                          <span className="source-badge pedido_users">{shift.shiftDate}</span>
                        </td>
                        <td className="col-receipts"></td>
                        <td className="col-avg"></td>
                        <td className="col-total">
                          <span className="receipt-tip">{formatCurrency(shift.tip)}</span>
                        </td>
                        <td className="col-share"></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {userRows.length > 0 && (
            <tfoot>
              <tr className="totals-row">
                <td className="col-expand"></td>
                <td className="col-user"><strong>Total</strong></td>
                <td className="col-receipts"><strong>{userRows.reduce((s, u) => s + u.shiftCount, 0)}</strong></td>
                <td className="col-avg"></td>
                <td className="col-total">
                  <strong className="grand-total">{formatCurrency(totalTip)}</strong>
                </td>
                <td className="col-share"><strong>100%</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default IndividualTipView;
